import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {
  IOrder,
  IOrderListPayload,
  IOrderPaymentType,
  IOrderClearRouteFieldErrorPayload,
  IOrderStatus
} from "../../models/order"
import {IValidationOrderFormErrors} from "../../models/validation"
import {AxiosError} from "axios"

interface IOrderState {
  list: IOrder[]
  order: IOrder
  pages: number
  totalCount: number
  pageSize: number
  currentPage: number
  totalPrice: number
  complete: number
  leads: number
  creatingFromCall: boolean
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isLoadingOrder: boolean
  error: any
  fieldError: any
  routeError: any
  paymentTypes: IOrderPaymentType[]
  statuses: IOrderStatus[]
  filters: any
}

const initialState: IOrderState = {
  list: [],
  order: {
    id: 0,
    client_uuid: "",
    comment: "",
    status_id: 0,
    brigade_id: 0,
    medical_name_from: "",
    medical_uuid_from: "",
    city_from: "",
    address_from: "",
    entrance_from: "",
    floor_from: "",
    flat_from: "",
    lift_from: false,
    medical_name_to: "",
    medical_uuid_to: "",
    city_to: "",
    address_to: "",
    entrance_to: "",
    floor_to: "",
    flat_to: "",
    lift_to: false,
    price: 0,
    payment_type_id: "",
    request_date: "",
    request_time: "",
    created_at: 0,
    updated_at: 0,
    client_fio: "",
    client_phone: "",
    client_birthday: "",
    client_height: "",
    client_weight: "",
    brigade: {
      id: 0,
      brigade: ""
    }
  },
  pages: 0,
  totalCount: 0,
  pageSize: 10,
  currentPage: 1,
  totalPrice: 0,
  complete: 0,
  leads: 0,
  creatingFromCall: false,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isLoadingOrder: false,
  error: {},
  fieldError: {},
  routeError: [],
  paymentTypes: [],
  statuses: [],
  filters: {}
}

export const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    fetching(state) {
      state.isLoading = true
    },
    fetchSuccess(state, action: PayloadAction<IOrderListPayload>) {
      state.isLoading = false
      state.error = {}
      state.list = action.payload.list || []
      state.pages = action.payload.pages
      state.totalCount = action.payload.totalCount
      state.totalPrice = action.payload.totalPrice
      state.complete = action.payload.complete
      state.leads = action.payload.leads
    },
    fetchError(state, action: PayloadAction<AxiosError>) {
      state.isLoading = false

      if (action.payload) {
        state.error = action.payload.message
      }
    },
    creating(state) {
      state.isCreating = true
    },
    createSuccess(state) {
      state.isCreating = false
    },
    creationError(state, action: PayloadAction<AxiosError>) {
      state.isCreating = false

      if (action.payload) {
        state.error = action.payload.message
      }
    },
    updating(state) {
      state.isUpdating = true
    },
    updateSuccess(state) {
      state.isUpdating = false
    },
    updateError(state, action: PayloadAction<AxiosError>) {
      state.isUpdating = false

      if (action.payload) {
        state.error = action.payload.message
      }
    },
    fetchPaymentTypesSuccess(state, action: PayloadAction<IOrderPaymentType[]>) {
      state.paymentTypes = action.payload
    },
    fetchStatusesSuccess(state, action: PayloadAction<IOrderStatus[]>) {
      state.statuses = action.payload
    },
    saveError(state, action: PayloadAction<IValidationOrderFormErrors>) {
      state.isCreating = false
      state.isUpdating = false
      const fieldErrors = action.payload.field_errors
      const routeErrors = action.payload.route_errors

      if (fieldErrors && fieldErrors.length) {
        fieldErrors.forEach(error => {
          state.fieldError[error.field] = error.message
        })
      }

      if (routeErrors && routeErrors.length) {
        state.routeError = routeErrors.map(routeError => {
          const obj: any = {}

          if (routeError.length) {
            routeError.forEach(item => (
              obj[item.field] = item.message
            ))
          }

          return obj
        })
      }
    },
    clearFieldError(state, action: PayloadAction<string>) {
      delete state.fieldError[action.payload]
    },
    clearRouteFieldError(state, action: PayloadAction<IOrderClearRouteFieldErrorPayload>){
      if (action.payload) {
        if (state.routeError[action.payload.routeID] && state.routeError[action.payload.routeID].hasOwnProperty(action.payload.fieldName))
          delete state.routeError[action.payload.routeID][action.payload.fieldName]
      }
    },
    clearAllFieldErrors(state) {
      state.fieldError = {}
    },
    clearAllRouteErrors(state) {
      state.routeError = []
    },
    setFilters(state, action: PayloadAction<any>) {

      for (let key in action.payload) {
        if (0 === action.payload[key] || "" === action.payload[key]) {
          delete action.payload[key]
        }
      }

      state.filters = action.payload
    },
    fetchingOrder(state){
      state.isLoadingOrder = true
      state.order = initialState.order
    },
    fetchOrderSuccess(state, action: PayloadAction<IOrder>) {
      state.isLoadingOrder = false
      state.order = action.payload
    },
    fetchOrderError(state, action: PayloadAction<AxiosError>) {
      state.isLoadingOrder = false

      if (action.payload) {
        state.error = action.payload.message
      }
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    setCreatingFromCallFlag(state, action: PayloadAction<boolean>) {
      state.creatingFromCall = action.payload
    }
  }
})

export default orderSlice.reducer